/**
 * Short "bell" tone played when a new notification arrives, so the badge
 * change has an audible cue and isn't easy to miss when the tab isn't focused.
 * Synthesized via Web Audio API — no audio asset to host/load.
 */

let sharedContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedContext) sharedContext = new Ctor();
  return sharedContext;
};

export const playNotificationSound = (): void => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // Browsers block audio until a user gesture has occurred on the page;
      // resume() is a no-op (rejected promise, silently ignored) until then.
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    // Two quick tones (a soft "ding-dong") rather than a single flat beep.
    [{ freq: 880, start: 0 }, { freq: 660, start: 0.12 }].forEach(({ freq, start }) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = freq;
      oscillator.connect(gain);
      gain.connect(ctx.destination);

      const t0 = now + start;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.15, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);

      oscillator.start(t0);
      oscillator.stop(t0 + 0.3);
    });
  } catch {
    // Ignore — sound is a nice-to-have, never worth crashing the bell over.
  }
};
